import { default as _set } from 'lodash/set'
import { produce } from 'immer'
import regEx from 'utils/global/regEx'

/**
 * Handles default checkbox toggling for services.
 * Enforces per-type defaults: one default for type="service", one for type="visitation".
 * When checking a service, only unchecks other services of the SAME type.
 * When unchecking, finds first OTHER of same type to make default.
 */
const handleServiceDefault = {
  checking: (variables) => {
    // if checking an unchecked box
    const { name, path, set, newValue, fieldValue, caseValuesAtom } = variables

    // Extract the index being checked from the path (e.g., "value[2].default" -> 2)
    const indexMatch = path.match(/value\[(\d+)\]/)
    const checkedIndex = indexMatch ? parseInt(indexMatch[1], 10) : -1

    const newField = produce(fieldValue, (draft) => {
      const services = draft.value
      const checkedService = services[checkedIndex]
      const checkedType = checkedService?.type?.toLowerCase()

      // Only uncheck services of the SAME type (not all services)
      services.forEach((service, i) => {
        const serviceType = service.type?.toLowerCase()
        if (serviceType === checkedType) {
          _set(draft, `value[${i}].default`, false)
        }
      })

      // set clicked checkbox to true
      _set(draft, path, newValue)
    })

    // set the checkboxes accordingly
    set(caseValuesAtom(name), newField)
  },

  unchecking: (variables) => {
    const { name, path, set, newValue, fieldValue, caseValuesAtom } =
      variables || {}

    // Extract the index being unchecked
    const uncheckedMatch = path.match(regEx.uncheckedMatch)
    const uncheckedIndex = uncheckedMatch ? parseInt(uncheckedMatch[1], 10) : -1
    const uncheckedService = fieldValue.value[uncheckedIndex]
    const uncheckedType = uncheckedService?.type?.toLowerCase()

    // Count how many services of this type exist
    const servicesOfSameType = fieldValue.value.filter(
      (s) => s.type?.toLowerCase() === uncheckedType,
    )

    // If this is the only service of this type, don't allow unchecking
    if (servicesOfSameType.length === 1) return

    const newField = produce(fieldValue, (draft) => {
      const services = draft.value

      // Find the first service of the SAME type that is not the one being unchecked
      const firstOtherOfSameType = services.findIndex((service, index) => {
        const isSameType = service.type?.toLowerCase() === uncheckedType
        const isDifferent = index !== uncheckedIndex
        return isSameType && isDifferent
      })

      if (firstOtherOfSameType >= 0) {
        _set(draft, `value[${firstOtherOfSameType}].default`, true)
      }

      _set(draft, path, newValue)
    })

    // set the checkboxes accordingly
    set(caseValuesAtom(name), newField)
  },
}

export const setDefaultService = (variables) => {
  const { name, path, newValue, setCaseDetail } = variables

  // determining if the case detail being set is a service checkbox
  const isServices = name === 'services'
  const isCheckBox = path.includes('default')
  const isServiceCheckbox = isServices && isCheckBox

  // determine if the user is checking or unchecking a service checkbox
  const checkingDefaultService = isServiceCheckbox && newValue
  const uncheckingDefaultService = isServiceCheckbox && !newValue

  // determine action needed
  if (checkingDefaultService) {
    handleServiceDefault.checking(variables)
  }
  if (uncheckingDefaultService) {
    handleServiceDefault.unchecking(variables)
  }

  // if it is not a service checkbox, proceed as usual
  if (!isServiceCheckbox) {
    setCaseDetail()
  }
}

export default setDefaultService
